<script setup lang="ts">
/* global google */
import type { MapMarker, Polyline } from "@/components/controls/map/maps";
import { computed, onBeforeUnmount, onMounted, watch } from "vue";
import { darkenHex } from "@/app/ui/colourUtils";
import { redPinColour } from "@/components/controls/map/maps";
import { isNotNullOrUndefined } from "@/app/apiutils";
import isEqual from "lodash-es/isEqual";
import environment from "@/environment";
import type { ClusterFeature } from "supercluster";
import Supercluster from "supercluster";
import type { Maybe } from "@/components/utils/typeHelpers";
import { makeRef } from "@/components/composables/reactivityTypes";

export interface MapLoaderInterface {
  setBounds: () => void;
}

type GoogleMapMarker = google.maps.marker.AdvancedMarkerElement;
type GoogleMapMarkerWithBmMarker = GoogleMapMarker & { marker: MapMarker };
type GoogleMapPolyline = google.maps.Polyline;
type GoogleMap = google.maps.Map;
type InfoWindow = google.maps.InfoWindow;

interface BmMapLoaderProps {
  value?: Maybe<MapMarker>;
  markers?: MapMarker[];
  polylines?: readonly Polyline[];
  updateBoundsOnMarkerUpdate?: boolean;
  visible?: boolean;
  useMarkerClusterer?: boolean;
}

interface BmMapLoaderEvents {
  (event: "update:value", value: Maybe<MapMarker>): void;
  (eventName: "place-click", placeId: string): void;
}

const props = withDefaults(defineProps<BmMapLoaderProps>(), {
  markers: () => [],
  polylines: () => [],
  updateBoundsOnMarkerUpdate: true,
  visible: true,
  useMarkerClusterer: false,
});

const emit = defineEmits<BmMapLoaderEvents>();

const googleMapRef = makeRef<Maybe<HTMLDivElement>>(null);
const googleMapPolylines = makeRef<readonly GoogleMapPolyline[]>([]);
const loading = makeRef(false);
const hoveredMarker = makeRef<Maybe<MapMarker>>(null);

// Keep a track of the markers currently on the map, so we can clean them up when needed.
let currentClusterMarkers: google.maps.Marker[] = [];
let currentPointMarkers: GoogleMapMarkerWithBmMarker[] = [];

const markerForInfoWindow = computed(() => hoveredMarker.value || props.value);

let gMap: Maybe<GoogleMap>;
let infoWindow: Maybe<InfoWindow>;

const defaultZoomLevel = 10;

async function initMap() {
  // Load our required libs
  const [MapsLibrary] = (await Promise.all([
    google.maps.importLibrary("maps"),
    google.maps.importLibrary("geometry"),
  ])) as [google.maps.MapsLibrary, google.maps.GeometryLibrary];

  // Check we have been torn down while loading.
  if (!googleMapRef.value) return;

  // Create the map
  gMap = new MapsLibrary.Map(googleMapRef.value, {
    // Sets default values for maps. These will be overridden by setting markers
    zoom: defaultZoomLevel,
    center: { lat: 0, lng: 0 },
    mapTypeControl: false,
    streetViewControl: false,
    gestureHandling: "cooperative",
    mapId: environment.apiGmapsId,
  });

  // disableAutoPan avoids a pan->idle->marker rebuild loop when opening the window
  infoWindow = new MapsLibrary.InfoWindow({ disableAutoPan: true });

  google.maps.event.addListenerOnce(gMap, "idle", () => {
    loading.value = false;
  });

  gMap.addListener("click", (e: any) => {
    if (e.placeId) {
      emit("place-click", e.placeId);
    }
    emit("update:value", null);
  });

  await updatePolylines();
  setBounds();

  focusCurrentMarker();

  gMap?.addListener("idle", () => updateMarkersForViewport());
  await updateMarkersForViewport();
}

function buildPinTitle(bmMarker: MapMarker) {
  const labelValue =
    bmMarker.showSequence === false
      ? undefined
      : (bmMarker.sequence?.toString() ?? "");

  return [bmMarker.pinLabelPrefix, labelValue]
    .filter(isNotNullOrUndefined)
    .join(" - ");
}

// Used for when a pin title exists, and manually creates scalable elements which can fit the entire text
function buildCustomPin(bmMarker: MapMarker) {
  const customPin = document.createElement("div");
  customPin.textContent = buildPinTitle(bmMarker);
  customPin.classList.add("google-map-pin");
  customPin.style.background = bmMarker.colour || redPinColour;

  const customPinPointer = document.createElement("div");
  customPinPointer.classList.add("google-map-pin-pointer");
  customPin.appendChild(customPinPointer);

  return customPin;
}

function markerClicked(googleMapMarker: GoogleMapMarkerWithBmMarker) {
  if (googleMapMarker.marker.meta?.placeId) {
    emit("place-click", googleMapMarker.marker.meta.placeId);
  }
  emit("update:value", googleMapMarker.marker);
}

function markerHovered(googleMarkerWithBmMarker: GoogleMapMarkerWithBmMarker) {
  hoveredMarker.value = googleMarkerWithBmMarker.marker;
}

function markerUnhovered(bmMarker: MapMarker) {
  hoveredMarker.value = null;
}

// Merges our marker into the google marker - enforces a type definition
function mergeGoogleMarkerWithMapMarker(
  googleMarker: GoogleMapMarker,
  bmMarker: MapMarker
): asserts googleMarker is GoogleMapMarkerWithBmMarker {
  // Force in our marker obj, this is quite iffy but extremely useful
  Object.assign(googleMarker, { marker: bmMarker });
}

/**
 * Builds an advanced marker from a given bmMarker. Does not add the marker
 * to the map.
 *
 * To add a marker to the map, use `marker.map = map`.
 */
function buildMarker(
  bmMarker: MapMarker,
  { AdvancedMarkerElement, PinElement }: google.maps.MarkerLibrary
) {
  if (bmMarker.lat === null || bmMarker.lng === null) {
    return;
  }

  const content =
    buildPinTitle(bmMarker) !== ""
      ? buildCustomPin(bmMarker)
      : new PinElement({
          // Default setup
          background: bmMarker.colour,
          borderColor: darkenHex(bmMarker.colour ?? redPinColour, 50),
          glyphColor: darkenHex(bmMarker.colour ?? redPinColour, 50),
        }).element;

  const googleMarker = new AdvancedMarkerElement({
    map: null,
    position: { lat: bmMarker.lat, lng: bmMarker.lng },
    content,
    gmpClickable: true,
    zIndex: bmMarker.sequence ? bmMarker.sequence + props.markers.length : -1,
  });

  mergeGoogleMarkerWithMapMarker(googleMarker, bmMarker);

  googleMarker.addEventListener("gmp-click", () => {
    markerClicked(googleMarker);
  });

  // pointerenter/leave don't re-fire when moving between child pin elements
  googleMarker.element.addEventListener("pointerenter", () => {
    markerHovered(googleMarker);
  });

  googleMarker.element.addEventListener("pointerleave", () => {
    markerUnhovered(bmMarker);
  });

  return googleMarker;
}

async function updatePolylines() {
  googleMapPolylines.value.forEach((m) => m.setMap(null));

  if (gMap) {
    googleMapPolylines.value = props.polylines.map(
      (bmPolyline) =>
        new google.maps.Polyline({
          path: google.maps.geometry.encoding.decodePath(
            bmPolyline.encodedPolyline
          ),
          strokeColor: bmPolyline.strokeColor,
          strokeOpacity: bmPolyline.strokeOpacity,
          strokeWeight: bmPolyline.strokeWeight,
          zIndex: bmPolyline.zIndex
            ? bmPolyline.zIndex + props.polylines.length
            : -1,
          map: gMap,
        })
    );
  }
}

const markersAreEqual = (
  markerOne: Maybe<MapMarker>,
  markerTwo: Maybe<MapMarker>
) =>
  markerOne &&
  markerTwo &&
  ((markerOne.key && markerTwo.key && markerOne.key === markerTwo.key) ||
    isEqual(markerOne, markerTwo));

const updateInfoWindow = () => {
  const bmMarker = markerForInfoWindow.value;

  const googleMarker = currentPointMarkers.find((googleMapMarkerWithBmMarker) =>
    markersAreEqual(googleMapMarkerWithBmMarker.marker, bmMarker)
  );

  if (bmMarker && googleMarker && gMap) {
    const content = document.createElement("div");
    content.textContent = bmMarker.name;
    content.classList.add("info-window-container");

    for (const line of bmMarker.hoverLines ?? []) {
      if (line.url) {
        const link = document.createElement("a");
        link.href = line.url;
        link.textContent = line.text;
        content.appendChild(link);
      } else {
        const divElement = document.createElement("div");
        divElement.textContent = line.text;
        content.appendChild(divElement);
      }
    }

    infoWindow?.setContent(content);
    infoWindow?.open({
      map: googleMarker.map,
      anchor: googleMarker,
      shouldFocus: false,
    });
  } else {
    infoWindow?.close();
  }
};

function focusCurrentMarker() {
  const bmMarker = props.value;
  if (bmMarker?.lat && bmMarker?.lng && gMap) {
    gMap.panTo({
      lat: bmMarker.lat,
      lng: bmMarker.lng,
    });
  }

  updateInfoWindow();
}

function setBounds() {
  // Don't update if we have no markers to set our bounds to
  if (!gMap || props.markers.length === 0) {
    return;
  }

  // Don't update bounds if not required, and zoom level has changed from default
  if (
    !props.updateBoundsOnMarkerUpdate &&
    gMap.getZoom() !== defaultZoomLevel
  ) {
    return;
  }

  let bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

  const firstPolylinePoint = googleMapPolylines.value.at(0)?.getPath().getAt(0);
  const linePoints = firstPolylinePoint ? [firstPolylinePoint] : [];
  const markerPoints = props.markers
    .filter((marker) => marker.includeInMapBounds && marker.lat && marker.lng)
    .map((marker) => new google.maps.LatLng(marker.lat!, marker.lng!));

  const fitPoints = linePoints.concat(markerPoints);

  for (const point of fitPoints) {
    bounds.extend(point);
  }

  // Ensure the map covers at least a range of lats and lngs.
  const lats = fitPoints.map((x) => x.lat());
  const lngs = fitPoints.map((x) => x.lng());
  if (fitPoints.length > 0) {
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const llPad = 0.002;
    bounds.extend(new google.maps.LatLng(maxLat - llPad, maxLng - llPad));
    bounds.extend(new google.maps.LatLng(minLat + llPad, minLng + llPad));
  }

  // Setting the map to fit around all of the points and adding padding of these given values
  gMap.fitBounds(bounds, {
    top: 30,
    right: 30,
    left: 30,
    bottom: 30,
  });
}

// Gemini suggests that the default map clustering, which works on pre-built map markers
// is simple to use, but not efficient for thousands of marker points.
// Instead may be better to cluster on the source marker info, and then generate the
// MapMarker elements for just the visible markers / clusters
// 1. Install Supercluster: npm install supercluster
const markerIndex = computed(() => {
  const points = props.markers
    .filter((x) => isNotNullOrUndefined(x.lat) && isNotNullOrUndefined(x.lng))
    .map((bmMarker) => ({
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: [bmMarker.lng!, bmMarker.lat!],
      },
      properties: {
        bmMarker,
      },
    }));
  const index = new Supercluster({
    radius: 60, // Cluster radius in pixels
    maxZoom: 16, // Max zoom to cluster points on
  });
  index.load(points);
  return index;
});

function createClusterMarker(cluster: ClusterFeature) {
  const [lng, lat] = cluster.geometry.coordinates;
  const clusterId = cluster.properties.cluster_id;
  const marker = new google.maps.Marker({
    position: { lat, lng },
    map: gMap,
    // Use the label to show the point count
    label: {
      text: cluster.properties.point_count_abbreviated.toString(),
      color: "white",
      fontSize: "8pt",
    },
    // Style the cluster icon
    icon: {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(
          '<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="18" fill="rgba(41, 41, 255, 0.9)" stroke="white" stroke-width="2"/></svg>'
        ),
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 20),
    },
    // Lower zIndex for clusters
    zIndex: 1,
  });

  // Make clusters clickable to zoom in
  marker.addListener("click", () => {
    if (gMap) {
      const expansionZoom =
        markerIndex.value.getClusterExpansionZoom(clusterId);
      gMap.setZoom(expansionZoom);
      gMap.panTo({ lat, lng });
    }
  });

  return marker;
}

function runClusterer() {
  let clusters: ClusterFeature[] = [];
  let points: MapMarker[] = [];

  const bounds = gMap?.getBounds();
  const zoom = gMap?.getZoom();

  if (bounds && isNotNullOrUndefined(zoom)) {
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    const clusterOrPoints = markerIndex.value.getClusters(
      [southWest.lng(), southWest.lat(), northEast.lng(), northEast.lat()],
      zoom
    );

    for (const clusterOrPoint of clusterOrPoints) {
      if (clusterOrPoint.properties.cluster) {
        clusters.push(clusterOrPoint);
      } else {
        points.push(clusterOrPoint.properties.bmMarker);
      }
    }
  }

  return { clusters, points };
}

async function updateMarkersForViewport() {
  const clusterResults = props.useMarkerClusterer ? runClusterer() : undefined;

  const clusters = clusterResults?.clusters ?? [];
  const points = clusterResults?.points ?? props.markers;

  const MarkerLibrary = (await google.maps.importLibrary(
    "marker"
  )) as google.maps.MarkerLibrary;

  // Remove the old markers from the map.
  for (const clusterMarker of currentClusterMarkers) {
    clusterMarker.setMap(null);
  }

  for (const pointMarker of currentPointMarkers) {
    pointMarker.map = null;
  }

  currentClusterMarkers = [];
  currentPointMarkers = [];

  if (gMap) {
    for (const cluster of clusters) {
      currentClusterMarkers.push(createClusterMarker(cluster));
    }
    for (const point of points) {
      const marker = buildMarker(point, MarkerLibrary);
      if (marker) {
        marker.map = gMap;
        currentPointMarkers.push(marker);
      }
    }

    // Re-open the stuck info window if the selected marker is still in view
    updateInfoWindow();
  }
}

onMounted(async () => {
  await initMap();
});

function teardownMap() {
  if (gMap) {
    google.maps.event.clearInstanceListeners(gMap);
    infoWindow?.close();
    for (const marker of currentClusterMarkers) marker.setMap(null);
    for (const polyline of googleMapPolylines.value) polyline.setMap(null);
    currentPointMarkers = [];
    currentClusterMarkers = [];
    gMap = null;
  }
}

onBeforeUnmount(teardownMap);

watch(
  () => props.markers,
  async () => {
    setBounds();
    await updateMarkersForViewport();
  }
);

watch(
  () => props.visible,
  async () => {
    await updatePolylines();
    setBounds();
    await updateMarkersForViewport();
  }
);

watch(
  () => props.polylines,
  async () => {
    await updatePolylines();
    setBounds();
    await updateMarkersForViewport();
  }
);

watch(() => props.value, focusCurrentMarker);

watch(() => markerForInfoWindow.value, updateInfoWindow);
</script>

<template>
  <div class="google-map-container">
    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <div
      ref="googleMapRef"
      class="google-map"
      :style="{ visibility: visible ? 'visible' : 'hidden' }"
    ></div>
  </div>
</template>

<style lang="sass">
.google-map-container
  height: 100%

.google-map
  min-width: 100px
  height: 100%
  min-height: 100%

.google-map-pin
  border: 1px solid rgba(0, 0, 0, 0.33)
  border-radius: 9999px
  font-size: 14px
  padding: 4px 8px
  color: white

.google-map-pin-pointer
  z-index: -1
  position: absolute
  background: inherit
  filter: brightness(85%)
  width: 20px
  left: calc(50% - 10px)
  height: 15px
  clip-path: polygon(0 0, 100% 0, 50% 100%)

.info-window-container
  display: flex
  flex-flow: column
  align-items: center
  gap: 2px
  color: #505457
</style>
